import {faPrint, faSms, faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useForm} from "react-hook-form";
import printjs from "print-js";
import React, {useEffect, useState} from "react";
import ReactModal from "react-modal";
import cn from "classnames";
import axios from "axios";
import moment from "moment";
import numeral from "numeral";
import {useSelector} from "react-redux";
import ReactTable from "react-table";
import "react-table/react-table.css";
import * as api from "../../../api/IrsClient";
import ModalError from "../../common/ModalError";
import Modal from "../../common/Modal";
import SearchForm from "./RemittanceSearchForm";
import {ACTIVITY_OPTION, KEYCLOAK_ROLES,} from "../../../constant";
import ModalNotFound from "../../common/ModalNotFound";

const reportDefault = {
  data: [],
  grand_total_amount_destination: "",
  grand_total_amount_original: "",
  grand_total_cashback_amount: "",
  grand_total_com: "",
  grand_total_fee: "",
  grand_total_net_amount: "",
  total_amount_destination: "",
  total_amount_original: "",
  total_cashback_amount: "",
  total_com: "",
  total_fee: "",
  total_net_amount: "",
};

function IrsRemittanceReport({ userRoles }) {
  const user = useSelector((state) => state.user);
  const [report, setReport] = useState(reportDefault);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [showPagination, setShowPagination] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState(null);
  const [selectedTransCancellation, setSelectedTransCancellation] =
    useState(null);
  const [isWallet, setIsWallet] = useState(null);
  const [selectedTransResendSms, setSelectedTransResendSms] = useState(null);
  const [isShownModalResendSmsSuccess, setIsShownModalResendSmsSuccess] =
    useState(false);
  const [notFound, setNotfound] = useState(null);

  useEffect(() => {
    if (!loading && !showPagination) {
      setShowPagination(true);
    }
  }, [loading, showPagination]);

  async function getReport(
    newPage = page,
    newPageSize = pageSize,
    value = searchValue
  ) {
    try {
      setLoading(true);
      const body = {
        ...value,
        item_per_page: newPageSize,
        page_no: newPage + 1,
      };
      const {
        data: { data, status },
      } = await api.getIrsRemittanceReport(body);
      if (status.code !== "0") {
        setError({ description: status.message });
        setLoading(false);
        return;
      }
      setSearchValue(body);

      if (!data.list_transaction_objects) {
        setNotfound({ title: "" });
        setReport(reportDefault);
      } else {
        setReport({
          data: data.list_transaction_objects,
          ...data.total_info,
        });
      }
      setPages(Math.ceil(data.total / data.total_per_page));
    } catch (error) {
      setError({ title: "System Error" });
    } finally {
      setLoading(false);
    }
  }

  async function downloadReport(values) {
    try {
      setLoading(true);
      const { data } = await api.getIrsRemittanceReportFile(values);
      if (!data) {
        setError({ title: "System Error" });
        setLoading(false);
        return false;
      }
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const reportDate = moment().format("YYYY-MM-DD-HH-mm-ss");
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.target = "_blank";
      a.href = downloadUrl;
      a.download = `remittance-exported-${reportDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
    } catch (error) {
      setError({ title: "System Error" });
    } finally {
      setLoading(false);
    }
  }

  async function resendSMS() {
    try {
      setLoading(true);
      const {
        data: { status },
      } = await api.resendSMS({
        transaction_id: selectedTransResendSms.transactionId,
      });
      if (status.code !== "0") {
        setError({ description: status.message });
      }
      setIsShownModalResendSmsSuccess(true);
      setSelectedTransResendSms(null);
      getReport();
    } catch (error) {
      setError({ title: "System Error" });
    } finally {
      closeResendSmsModal();
      setLoading(false);
    }
  }

  async function cancelTransfer(values) {
    try {
      setLoading(true);
      const cancelTransferInfo = {
        request_trans_id: values.transactionId,
        remark: values.remark,
        cancel_by: user.preferred_username,
      };
      if(values.isWallet){
        const {
          data: { status },
        } = await api.cancelTransferWallet(cancelTransferInfo);
        if (status.code !== "0") {
          setError({ description: status.message });
        }
      }
      else{
        const {
          data: { status },
        } = await api.cancelTransfer(cancelTransferInfo);
        if (status.code !== "0") {
          setError({ description: status.message });
        }
      }
      getReport();
    } catch (error) {
      console.log(error);
      setError({ title: "System Error" });
    } finally {
      closeTransCancellationModal();
      setLoading(false);
    }
  }

  async function printSlip(transactionId) {
    try {
      setLoading(true);
      const {
        data: {
          data: { url },
        },
      } = await api.getSlip(transactionId, {
        copy: true,
      });
      const stream = await axios.get(url, {
        responseType: "blob",
      });
      const file = new Blob([stream.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      printjs({
        printable: fileURL,
        type: "pdf",
      });
    } catch (error) {
      setError({ title: "System Error" });
    } finally {
      setLoading(false);
    }
  }

  function closeTransCancellationModal() {
    setSelectedTransCancellation(null);
    setIsWallet(null);
  }

  function closeResendSmsModal() {
    setSelectedTransResendSms(null);
  }

  function search(values) {
    setPage(0);
    getReport(0, pageSize, values);
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    getReport(newPage);
  }

  function handlePageSizeChange(newPageSize) {
    setPage(0);
    setPageSize(newPageSize);
    getReport(0, newPageSize);
  }

  function columnsStyle(color) {
    return (row) => {
      row.styles["backgroundColor"] = color;
      return row.value;
    };
  }

  function isCancelDisabled(status) {
    const enableTransactionStatus = [
      "TRANSACTION UPDATED",
      "SUCCESS REMITTANCE PRECONFIRM",
      "FAIL CANCEL",
    ];
    return !enableTransactionStatus.includes(status);
  }

  function isResendSmsDisabled(status) {
    const enableTransactionStatus = [
      "TRANSACTION UPDATED",
      "SUCCESS REMITTANCE PRECONFIRM",
    ];
    return !enableTransactionStatus.includes(status);
  }

  function isRePrintSlipDisabled(status) {
    const disableTransactionStatus = ["TRANSACTION CREATED", "FAIL REMITTANCE"];
    return disableTransactionStatus.includes(status);
  }

  function renderActivity(status) {
    if (!status) return "";
    const text = ACTIVITY_OPTION.find((x) => x.value === status).label;
    return text;
  }

  function getColumnWidth(rows, accessor, headerText) {
    const maxWidth = 400;
    const magicSpacing = 10;
    const tmp = accessor.split(".");
    if (!rows) return;
    const cellLength = Math.max(
      ...rows.map((row) => (`${row[tmp[0]][tmp[1]]}` || "").length),
      headerText.length
    );
    return Math.min(maxWidth, cellLength * magicSpacing);
  }

  function clearReport() {
    setReport(reportDefault);
    setSearchValue(null);
    setPage(0);
    setPages(1);
    setShowPagination(false);
  }

  const columns = [
    {
      Header: "",
      headerStyle: {
        backgroundColor: "#D98880",
      },
      columns: [
        {
          Header: "#",
          accessor: "index",
          align: "center",
          maxWidth: 50,
          headerStyle: {
            backgroundColor: "#D98880",
          },
          Cell: (props) => {
            return props.index + 1 + page * pageSize;
          },
        },
      ],
    },
    {
      Header: "",
      headerStyle: {
        backgroundColor: "#84b4e1",
      },
      columns: [
        {
          Header: "Slip",
          show: userRoles?.includes(KEYCLOAK_ROLES.API_REPRINT_SLIP),
          accessor: "re_print_slip",
          align: "center",
          maxWidth: 80,
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          Cell: (props) => {
            return (
              <button
                disabled={
                  isRePrintSlipDisabled(
                    props.original.transaction_info.transaction_status
                  ) || loading
                }
                className="btn btn-icon btn-success"
                onClick={() => printSlip(props.original.irs_transaction_id)}
              >
                <FontAwesomeIcon icon={faPrint} />
              </button>
            );
          },
        },
      ],
    },
    {
      Header: "Transaction",
      headerStyle: {
        backgroundColor: "#f3a672",
      },
      columns: [
        {
          Header: "Created Date",
          accessor: "original_info.created_date",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Created Time",
          accessor: "original_info.created_time",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Updated Date",
          accessor: "original_info.updated_date",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Updated Time",
          accessor: "original_info.updated_time",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "IRS Trans ID",
          accessor: "irs_transaction_id",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "IRS Wallet Trans",
          accessor: "transaction_info.irs_wallet_transaction",
          width: getColumnWidth(
            report.data,
            "transaction_info.irs_wallet_transaction",
            "IRS Wallet Trans"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Utiba Trans",
          accessor: "original_info.utiba_trans",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Utiba Cancel Trans",
          accessor: "transaction_info.refund_utiba_id",
          width: getColumnWidth(
            report.data,
            "transaction_info.refund_utiba_id",
            "Utiba Cancel Trans"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
          Cell: columnsStyle("#fbe1d0"),
        },
        {
          Header: "Merchant Order Id",
          accessor: "transaction_info.merchant_order_id",
          width: getColumnWidth(
            report.data,
            "transaction_info.merchant_order_id",
            "Merchant Order Id"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Merchant Payment Id",
          accessor: "transaction_info.merchant_payment_id",
          width: getColumnWidth(
            report.data,
            "transaction_info.merchant_payment_id",
            "Merchant Payment Id"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Merchant Cancel Id",
          accessor: "transaction_info.merchant_refund_id",
          width: getColumnWidth(
            report.data,
            "transaction_info.merchant_refund_id",
            "Merchant Cancel Id"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
      ],
    },
    {
      Header: "Agent Information",
      headerStyle: {
        backgroundColor: "#aed293",
      },
      columns: [
        {
          Header: "Agent ID",
          accessor: "original_info.agent_id",
          headerStyle: {
            backgroundColor: "#aed293",
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>Total:</div>
                <div>Grand Total:</div>
              </div>
            );
          },
        },
        {
          Header: "Agent Name",
          accessor: "original_info.agent_name",
          headerStyle: {
            backgroundColor: "#aed293"
          }
        },
        {
          Header: "User ID",
          accessor: "original_info.user_id",
          headerStyle: {
            backgroundColor: "#aed293"
          }
        },
        {
          Header: "Com",
          accessor: "original_info.com",
          headerStyle: {
            backgroundColor: "#aed293",
          },
          Cell: (props) => {
            return numeral(props.original.original_info.com).format("0,0.00");
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_com}</div>
                <div>{report.grand_total_com}</div>
              </div>
            );
          },
        },
        {
          Header: "Fee",
          accessor: "original_info.fee",
          headerStyle: {
            backgroundColor: "#aed293",
          },
          Cell: (props) => {
            return numeral(props.original.original_info.fee).format("0,0.00");
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_fee}</div>
                <div>{report.grand_total_fee}</div>
              </div>
            );
          },
        },
        {
          Header: "Balance",
          accessor: "balance",
          headerStyle: {
            backgroundColor: "#aed293",
          },
          Cell: (props) => {
            return numeral(props.original.balance).format("0,0.00");
          },
        },
      ],
    },
    {
      Header: "Sender Information",
      headerStyle: {
        backgroundColor: "#84b4e1",
      },
      columns: [
        {
          Header: "ID",
          accessor: "original_info.sender_id",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
        },
        {
          Header: "Name",
          accessor: "original_info.customer_name",
          width: getColumnWidth(
            report.data,
            "original_info.customer_name",
            "Name"
          ),
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
        },
        {
          Header: "Mobile Number",
          accessor: "original_info.telephone",
          width: getColumnWidth(
            report.data,
            "original_info.telephone",
            "Mobile Number"
          ),
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
        },
      ],
    },
    {
      Header: "Receiver information",
      headerStyle: {
        backgroundColor: "#f3a672",
      },
      columns: [
        {
          Header: "ID",
          accessor: "destination_info.receiver_id",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Name",
          accessor: "destination_info.customer_name",
          width: getColumnWidth(
            report.data,
            "destination_info.customer_name",
            "Name"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Mobile Number",
          accessor: "destination_info.receiver_mobile",
          width: getColumnWidth(
            report.data,
            "destination_info.receiver_mobile",
            "Mobile Number"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Township",
          accessor: "destination_info.receiver_township",
          width: getColumnWidth(
            report.data,
            "destination_info.receiver_township",
            "Township"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
        {
          Header: "Country",
          accessor: "destination_info.country",
          width: getColumnWidth(
            report.data,
            "destination_info.country",
            "Country"
          ),
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
      ],
    },
    {
      Header: "Customer Rate",
      headerStyle: {
        backgroundColor: "#aed293",
      },
      columns: [
        {
          Header: "Rate",
          accessor: "exchange_rate_info.rate",
          headerStyle: {
            backgroundColor: "#aed293",
          },
          Cell: (props) => {
            return numeral(props.original.exchange_rate_info.rate).format(
              "0,0.00"
            );
          },
        },
        {
          Header: "Date",
          accessor: "exchange_rate_info.date",
          headerStyle: {
            backgroundColor: "#aed293",
          },
        },
      ],
    },
    {
      Header: "Original Amount",
      headerStyle: {
        backgroundColor: "#84b4e1",
      },
      columns: [
        {
          Header: "Amount",
          accessor: "original_info.amount",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          Cell: (props) => {
            return numeral(props.original.original_info.amount).format(
              "0,0.00"
            );
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_amount_original}</div>
                <div>{report.grand_total_amount_original}</div>
              </div>
            );
          },
        },
        {
          Header: "Cashback Amount",
          accessor: "original_info.cashback_amount",
          width: getColumnWidth(
            report.data,
            "original_info.cashback_amount",
            "Cashback Amount"
          ),
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          Cell: (props) => {
            return numeral(props.original.original_info.cashback_amount).format(
              "0,0.00"
            );
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_cashback_amount || ""}</div>
                <div>{report.grand_total_cashback_amount || ""}</div>
              </div>
            );
          },
        },
        {
          Header: "Net Amount",
          accessor: "original_info.net_amount",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          Cell: (props) => {
            return numeral(props.original.original_info.net_amount).format(
              "0,0.00"
            );
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_net_amount}</div>
                <div>{report.grand_total_net_amount}</div>
              </div>
            );
          },
        },
        {
          Header: "Currency",
          accessor: "original_info.currency",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
        },
      ],
    },
    {
      Header: "Destination Amount",
      headerStyle: {
        backgroundColor: "#f3a672",
      },
      columns: [
        {
          Header: "Amount",
          accessor: "destination_info.amount",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
          Cell: (props) => {
            return numeral(props.original.destination_info.amount).format(
              "0,0.00"
            );
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_amount_destination}</div>
                <div>{report.grand_total_amount_destination}</div>
              </div>
            );
          },
        },
        {
          Header: "Currency",
          accessor: "destination_info.currency",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
        },
      ],
    },
    {
      Header: "Transaction Detail",
      headerStyle: {
        backgroundColor: "#aed293",
      },
      columns: [
        {
          Header: "Transaction Status",
          accessor: "transaction_info.transaction_status",
          width: getColumnWidth(
            report.data,
            "transaction_info.transaction_status",
            "Transaction Status"
          ),
          headerStyle: {
            backgroundColor: "#aed293",
          },
        },
        {
          Header: "Status",
          accessor: "transaction_info.status",
          width: getColumnWidth(
            report.data,
            "transaction_info.status",
            "Status"
          ),
          headerStyle: {
            backgroundColor: "#aed293",
          },
        },
        {
          Header: "Cashout Date",
          accessor: "transaction_info.cashout_date",
          width: getColumnWidth(
              report.data,
              "transaction_info.cashout_date",
              "Cashout Date"
          ),
          headerStyle: {
            backgroundColor: "#aed293"
          }
        },
        {
          Header: "Cashout Time",
          accessor: "transaction_info.cashout_time",
          width: getColumnWidth(
              report.data,
              "transaction_info.cashout_time",
              "Cashout Time"
          ),
          headerStyle: {
            backgroundColor: "#aed293"
          }
        },
        {
          Header: "Trans Desc",
          accessor: "transaction_info.trans_desc",
          width: getColumnWidth(
            report.data,
            "transaction_info.trans_desc",
            "Trans Desc"
          ),
          headerStyle: {
            backgroundColor: "#aed293",
          },
        },
        {
          Header: "Activity",
          accessor: "transaction_info.activity",
          headerStyle: {
            backgroundColor: "#aed293",
          },
          Cell: (props) => {
            return renderActivity(props.original.transaction_info.activity);
          },
          renderActivity,
        },
        {
          Header: "Refund",
          accessor: "transaction_info.refund",
          headerStyle: {
            backgroundColor: "#aed293",
          },
        },
        {
          Header: "Cancelled Date",
          accessor: "transaction_info.cancel_date",
          width: getColumnWidth(
              report.data,
              "transaction_info.cancel_date",
              "Cancelled Date"
          ),
          headerStyle: {
            backgroundColor: "#aed293"
          }
        },
        {
          Header: "Cancelled Time",
          accessor: "transaction_info.cancel_time",
          width: getColumnWidth(
              report.data,
              "transaction_info.cancel_time",
              "Cancelled Time"
          ),
          headerStyle: {
            backgroundColor: "#aed293"
          }
        },
        {
          Header: "Cancelled By",
          accessor: "transaction_info.cancel_by",
          headerStyle: {
            backgroundColor: "#aed293"
          }
        },
        {
          Header: "Cancelled Reason",
          accessor: "transaction_info.remark",
          width: getColumnWidth(
              report.data,
              "transaction_info.remark",
              "Cancelled Reason"
          ),
          headerStyle: {
            backgroundColor: "#aed293",
          },
        },
      ],
    },
    {
      Header: "Bank Cost Rate",
      headerStyle: {
        backgroundColor: "#84b4e1",
      },
      columns: [
        {
          Header: "Bank Cost Rate",
          accessor: "bank_cost_rate_info.bank_cost_rate",
          width: getColumnWidth(
            report.data,
            "bank_cost_rate_info.bank_cost_rate",
            "Bank Cost Rate"
          ),
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          Cell: (props) => {
            return numeral(
              props.original.bank_cost_rate_info.bank_cost_rate
            ).format("0,0.00000000");
          },
        },
        {
          Header: "Agent Cost Coverage",
          accessor: "bank_cost_rate_info.agent_cost_coverage",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          width: getColumnWidth(
            report.data,
            "bank_cost_rate_info.agent_cost_coverage",
            "Agent Coverage Cost"
          ),
          Cell: (props) => {
            return numeral(
              props.original.bank_cost_rate_info.agent_cost_coverage
            ).format("0,0.00");
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_agent_coverage_cost}</div>
                <div>{report.grand_total_agent_coverage_cost}</div>
              </div>
            );
          },
        },
        {
          Header: "Transfer Fee (THB)",
          accessor: "bank_cost_rate_info.transfer_fee",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          width: getColumnWidth(
            report.data,
            "bank_cost_rate_info.transfer_fee",
            "Transfer Fee (THB)"
          ),
          Cell: (props) => {
            return numeral(
              props.original.bank_cost_rate_info.transfer_fee
            ).format("0,0.00");
          },
        },
        {
          Header: "Bank Cost Amount (THB)",
          accessor: "bank_cost_rate_info.bank_cost_amount",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          width: getColumnWidth(
            report.data,
            "bank_cost_rate_info.bank_cost_amount",
            "Cost Amount (THB)"
          ),
          Cell: (props) => {
            return numeral(
              props.original.bank_cost_rate_info.bank_cost_amount
            ).format("0,0.00");
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_bank_cost_amount}</div>
                <div>{report.grand_total_bank_cost_amount}</div>
              </div>
            );
          },
        },
        {
          Header: "FX Income (THB)",
          accessor: "bank_cost_rate_info.fx_income_thb",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          width: getColumnWidth(
            report.data,
            "bank_cost_rate_info.fx_income_thb",
            "FX Income (THB)"
          ),
          Cell: (props) => {
            return numeral(
              props.original.bank_cost_rate_info.fx_income_thb
            ).format("0,0.00");
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_fx_income_thb}</div>
                <div>{report.grand_total_fx_income_thb}</div>
              </div>
            );
          },
        },
        {
          Header: "FX Income (Foreign CCY)",
          accessor: "bank_cost_rate_info.fx_income_foreign",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          width: getColumnWidth(
            report.data,
            "bank_cost_rate_info.fx_income_foreign",
            "FX Income (Foreign CCY)"
          ),
          Cell: (props) => {
            return numeral(
              props.original.bank_cost_rate_info.fx_income_foreign
            ).format("0,0.00");
          },
          Footer: () => {
            return (
              <div className="report-footer">
                <div>{report.total_fx_income_foreign}</div>
                <div>{report.grand_total_fx_income_foreign}</div>
              </div>
            );
          },
        },
        {
          Header: "FX Income (%)",
          accessor: "bank_cost_rate_info.fx_income_percentage",
          headerStyle: {
            backgroundColor: "#84b4e1",
          },
          width: getColumnWidth(
            report.data,
            "bank_cost_rate_info.fx_income_percentage",
            "FX Income (Local CCY)"
          ),
          Cell: (props) => {
            return numeral(
              props.original.bank_cost_rate_info.fx_income_percentage
            ).format("0,0.00");
          },
        },
      ],
    },
    {
      Header: "",
      headerStyle: {
        backgroundColor: "#f3a672",
      },
      columns: [
        {
          Header: "Resend SMS",
          show: userRoles?.includes(KEYCLOAK_ROLES.API_RESEND_SMS),
          accessor: "index",
          align: "center",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
          Cell: (props) => {
            const data = {
              transactionId: props.original.irs_transaction_id,
              name: props.original.original_info.customer_name,
              mobile_number: props.original.original_info.telephone,
            };
            return (
              <button
                className="btn btn-icon btn-warning"
                onClick={() => setSelectedTransResendSms(data)}
                disabled={
                  isResendSmsDisabled(
                    props.original.transaction_info.transaction_status
                  ) || loading
                }
              >
                <FontAwesomeIcon icon={faSms} />
              </button>
            );
          },
        },
        {
          Header: "Cancel Trans",
          show: userRoles?.includes(KEYCLOAK_ROLES.API_CANCEL_TRANSACTION),
          accessor: "index",
          align: "center",
          headerStyle: {
            backgroundColor: "#f3a672",
          },
          Cell: (props) => {
            const transactionId = props.original.irs_transaction_id;
            const isWallet = props.original.transaction_info.irs_wallet_transaction;
            return (
              <button
                className="btn btn-icon btn-danger"
                type="button"
                onClick={() => { setSelectedTransCancellation(transactionId);
                  setIsWallet(isWallet);
                }}
                disabled={
                  isCancelDisabled(
                    props.original.transaction_info.transaction_status
                  ) || loading
                }
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            );
          },
        },
      ],
    },
  ];

  function initalDestinationCountry() {
    if (userRoles.includes(KEYCLOAK_ROLES.VIEW_IRS_MYANMAR_TRANSACTION_RPT)) {
      return "95";
    } else if (
      userRoles.includes(KEYCLOAK_ROLES.VIEW_IRS_CAMBODIA_TRANSACTION_RPT)
    ) {
      return "855";
    }
    return "95";
  }

  return (
    <div>
      <ModalNotFound
          message={"Data not found."}
          isOpen={!!notFound}
          description={notFound?.description ?? ""}
          onRequestClose={() => setNotfound(null)}
      />
      <ModalError
        message={error?.title ?? "System Error"}
        isOpen={!!error}
        description={error?.description ?? ""}
        onRequestClose={() => setError(null)}
      />
      <Modal
        message="Do you want to resend SMS?"
        isOpen={!!selectedTransResendSms}
        description={`Sender name: ${
          selectedTransResendSms?.name ?? ""
        }\nSender Mobile Number: ${
          selectedTransResendSms?.mobile_number ?? ""
        }`}
        onRequestClose={closeResendSmsModal}
        onCancel={closeResendSmsModal}
        onOk={resendSMS}
      />
      <Modal
        message="Resend SMS Success"
        isOpen={isShownModalResendSmsSuccess}
        onRequestClose={() => setIsShownModalResendSmsSuccess(false)}
        onOk={() => setIsShownModalResendSmsSuccess(false)}
        textOk="​Close"
      />
      <TransactionCancellationModal
        isOpen={!!selectedTransCancellation}
        onCancel={closeTransCancellationModal}
        cancelTransfer={cancelTransfer}
        transactionId={selectedTransCancellation}
        loading={loading}
        isWallet={isWallet}
      />
      <div className="card card-form">
        <div className="card-header">
          <h5>International Remittance Report</h5>
        </div>
        <SearchForm
          search={search}
          loading={loading}
          download={downloadReport}
          setErrorModal={setError}
          exportable={userRoles?.includes(
            KEYCLOAK_ROLES.API_EXPORT_IRS_TRANSACTION
          )}
          resetReport={clearReport}
          initalDestinationCountry={initalDestinationCountry()}
          userRoles={userRoles}
        />
      </div>
      <div className="card mt-2">
        <div className="card-body">
          <div className="row">
            <div className="col-md-12">
              <ReactTable
                className="-highlight transaction-report"
                columns={columns}
                data={report.data}
                defaultPageSize={10}
                loading={loading}
                manual
                minRows={5}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                page={page}
                pages={pages}
                pageSize={pageSize}
                pageSizeOptions={[10, 25, 50, 100]}
                resizable={true}
                sortable={false}
                showPageJump={true}
                showPagination={showPagination}
                showPaginationBottom={false}
                showPaginationTop={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const TransactionCancellationModal = ({
  isOpen,
  onCancel,
  transactionId,
  isWallet,
  cancelTransfer,
  loading,
}) => {
  function onSubmit(values) {
    cancelTransfer({
      transactionId: transactionId,
      remark: values.remark,
      isWallet: isWallet
    });
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      remark: "",
    },
  });

  useEffect(() => {
    reset();
  }, [isOpen]);

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onCancel}
      ariaHideApp={false}
      className="primary-modal"
      style={{
        overlay: {
          zIndex: "9999",
        },
      }}
    >
      <button
        type="button"
        className="close"
        aria-label="Close"
        onClick={onCancel}
      >
        <span aria-hidden="true">×</span>
      </button>
      <form onSubmit={handleSubmit(onSubmit)}>
        <p>
          <strong>
            Do you want to cancel transaction ID "{transactionId}" ?
          </strong>
        </p>
        <div
          className={cn("mb-4", {
            "form-group-error": errors?.remark,
          })}
        >
          <label className="mb-2">Remark</label>
          <div style={{ position: "relative" }}>
            <textarea
              data-testid="remark"
              rows={4}
              className="form-control"
              {...register("remark", { required: true })}
            />
            {errors?.remark && (
              <span className="input-error-message">Please specify value</span>
            )}
          </div>
        </div>
        <div className="text-lg-right">
          <button className="btn btn-warning" disabled={loading}>
            Submit
          </button>
        </div>
      </form>
    </ReactModal>
  );
};

export default IrsRemittanceReport;
